import ReactMarkdown from "react-markdown"

import { CoviVizChartBody, parseChartSpec } from "@/app-covi"
import {
  Button,
  Card,
  CardDescription,
  CardHeader,
  CardTitle,
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
  SofiaMark,
} from "@coval/ui"
import { ChevronDown, Database, ExternalLink } from "lucide-react"
import remarkGfm from "remark-gfm"

import { formatDate } from "../../utils/reportClient"
import type { Notebook } from "../../utils/reportTypes"
import { CoverageNotice } from "./CoverageNotice"
import { ReportSourceData } from "./ReportSourceData"
import { reportClass } from "./ReportStyles"

function safeUrl(value: string) {
  try {
    return ["https:", "http:"].includes(new URL(value).protocol)
      ? value
      : undefined
  } catch {
    return undefined
  }
}

function Coverage({ coverage }: { coverage: Record<string, unknown> }) {
  const display = [
    ["Records in scope", coverage.scope_records],
    ["Records this week", coverage.current_week_records],
    ["Selected agents", coverage.selected_agent_count],
    ["Observed numeric metrics", coverage.metric_count_available],
    [
      "Requested history starts",
      typeof coverage.history_start === "string"
        ? formatDate(coverage.history_start)
        : undefined,
    ],
    [
      "Data captured",
      typeof coverage.extracted_at === "string"
        ? `${formatDate(coverage.extracted_at, true)} PT`
        : undefined,
    ],
  ].filter((entry): entry is [string, string | number | boolean] =>
    ["string", "number", "boolean"].includes(typeof entry[1]),
  )
  return (
    <dl className={reportClass("coverage-grid")}>
      {display.slice(0, 12).map(([key, value]) => (
        <div key={key}>
          <dt>{key.replaceAll("_", " ")}</dt>
          <dd>
            {typeof value === "number" ? value.toLocaleString() : String(value)}
          </dd>
        </div>
      ))}
    </dl>
  )
}

/** Renders a saved generated notebook exactly as it was stored. */
export function NotebookDocument({ notebook }: { notebook: Notebook }) {
  return (
    <article
      className={reportClass("notebook-document")}
      data-font={notebook.presentation?.font}
      data-accent={notebook.presentation?.accent}
      data-density={notebook.presentation?.density}
    >
      <header className={reportClass("document-header")}>
        <div className={reportClass("eyebrow")}>
          <SofiaMark size={20} />
          Sofia notebook
        </div>
        <div className={reportClass("period-label")}>
          {formatDate(notebook.period.start)} –{" "}
          {formatDate(
            new Date(
              new Date(notebook.period.end).getTime() - 1000,
            ).toISOString(),
          )}{" "}
          <span>·</span> Pacific time
        </div>
        <h2>{notebook.title}</h2>
        <div className={reportClass("document-summary markdown")}>
          <ReactMarkdown remarkPlugins={[remarkGfm]}>
            {notebook.summary}
          </ReactMarkdown>
        </div>
        <div className={reportClass("document-meta")}>
          {notebook.coverage.comparison_available !== false && (
            <span>
              Compared with {formatDate(notebook.period.previous_start)} –{" "}
              {formatDate(
                new Date(
                  new Date(notebook.period.start).getTime() - 1000,
                ).toISOString(),
              )}
            </span>
          )}
          <span>Revision {notebook.revision}</span>
        </div>
        <CoverageNotice coverage={notebook.coverage} />
      </header>
      <div className={reportClass("document-sections")}>
        {notebook.sections.map((section, index) => {
          const spec = section.chart
            ? parseChartSpec(section.chart, { allowReportColors: true })
            : null
          const evidence = notebook.source_refs
            .filter((source) => section.evidence_ids?.includes(source.id))
            .filter(
              (source, position, sources) =>
                sources.findIndex((item) => item.url === source.url) ===
                position,
            )
          return (
            <section
              key={section.id}
              className={reportClass("document-section")}
            >
              <div className={reportClass("section-heading")}>
                <span className={reportClass("section-number")}>
                  {String(index + 1).padStart(2, "0")}
                </span>
                <h3>{section.title}</h3>
              </div>
              <div className={reportClass("markdown section-prose")}>
                <ReactMarkdown remarkPlugins={[remarkGfm]}>
                  {section.body}
                </ReactMarkdown>
              </div>
              {spec ? (
                <Card className={reportClass("chart-card")}>
                  <CardHeader>
                    <CardTitle>{spec.title}</CardTitle>
                    {spec.subtitle && (
                      <CardDescription>{spec.subtitle}</CardDescription>
                    )}
                  </CardHeader>
                  <CoviVizChartBody
                    reportColors
                    spec={spec}
                    height={Math.max(
                      260,
                      spec.opts?.horizontal ? spec.data.length * 38 + 80 : 280,
                    )}
                  />
                  <ReportSourceData spec={spec} />
                </Card>
              ) : (
                section.chart && (
                  <p className={reportClass("inline-error")}>
                    This chart did not pass validation. Its text and source
                    references are still available.
                  </p>
                )
              )}
              {evidence.length > 0 && (
                <div className={reportClass("evidence-links")}>
                  {evidence.map((source) => (
                    <Button
                      key={source.id}
                      variant="secondary"
                      asChild
                      className="h-auto max-w-full whitespace-normal px-2 py-1 text-xs"
                    >
                      <a
                        href={safeUrl(source.url)}
                        target="_blank"
                        rel="noreferrer"
                      >
                        <ExternalLink size={11} />
                        {source.label}
                      </a>
                    </Button>
                  ))}
                </div>
              )}
            </section>
          )
        })}
      </div>
      {Boolean(notebook.caveats?.length) && (
        <section className={reportClass("caveats")}>
          <h3>Keep in mind</h3>
          <ul>
            {notebook.caveats?.map((caveat) => (
              <li key={caveat}>{caveat}</li>
            ))}
          </ul>
        </section>
      )}
      <footer className={reportClass("provenance")}>
        <div className={reportClass("panel-heading")}>
          <Database size={14} />
          <h3>Sources & methodology</h3>
        </div>
        <p>
          Generated {formatDate(notebook.created_at, true)} Pacific time ·{" "}
          {notebook.model || "Model not recorded"} ·{" "}
          {notebook.generation_status?.replaceAll("_", " ")}
        </p>
        <p>
          AI narrative: review against evidence. Chart values are validated
          against the saved datasets; prose remains reviewable.
        </p>
        <Coverage coverage={notebook.coverage ?? {}} />
        {notebook.source_refs.length > 0 && (
          <Collapsible className="my-4">
            <CollapsibleTrigger asChild>
              <Button
                variant="ghost"
                className="max-w-full whitespace-normal text-left [&[data-state=open]>svg]:rotate-180"
              >
                <ChevronDown />
                All {notebook.source_refs.length} evidence references
              </Button>
            </CollapsibleTrigger>
            <CollapsibleContent>
              <ul>
                {notebook.source_refs.map((source) => (
                  <li key={source.id}>
                    <Button
                      variant="link"
                      asChild
                      className="max-w-full whitespace-normal text-left"
                    >
                      <a
                        href={safeUrl(source.url)}
                        target="_blank"
                        rel="noreferrer"
                      >
                        {source.label} <ExternalLink size={11} />
                      </a>
                    </Button>
                  </li>
                ))}
              </ul>
            </CollapsibleContent>
          </Collapsible>
        )}
        <p className={reportClass("field-hint")}>
          This artifact preserves its data window and revision. A later issue is
          generated separately; it does not rewrite this one.
        </p>
      </footer>
    </article>
  )
}
